import { TRANSITION_M } from "../config/constants";
import type { DensityParams, DifficultyFile, DifficultyParams, Grade, ModeConfig } from "../config/types";
import type { ZoneInfo } from "./zones";

export interface DifficultyInput {
    zone: ZoneInfo;
    grade: Grade | null;
    distanceM: number;
    bigfootAggression: number;
}

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

export function zoneRamp({ zoneProgress, intoM, blend }: ZoneInfo, cfg: DifficultyFile): number {
    const { start, end, transitionDip } = cfg.zoneRamp;
    const ramp = lerp(lerp(start, end, zoneProgress), start, blend);
    const nearBorder = Math.max(blend, clamp(1 - intoM / TRANSITION_M, 0, 1));
    return ramp * lerp(1, transitionDip, nearBorder);
}

export function freerideScale(distanceM: number, cfg: DifficultyFile): number {
    const { bands, softCapRate, softCapExtra } = cfg.freeride;
    const last = bands[bands.length - 1];
    if (distanceM >= last.fromM) {
        return last.scale + softCapExtra * (1 - Math.exp(-softCapRate * (distanceM - last.fromM)));
    }
    const i = bands.findIndex((band) => band.fromM > distanceM) - 1;
    const a = bands[Math.max(0, i)];
    const b = bands[Math.max(0, i) + 1];
    return lerp(a.scale, b.scale, clamp((distanceM - a.fromM) / (b.fromM - a.fromM), 0, 1));
}

function blendedBase({ zone, next, blend }: ZoneInfo, cfg: DifficultyFile): DensityParams {
    const from = cfg.zoneBase[zone];
    const to = next ? cfg.zoneBase[next] : from;
    const out = {} as DensityParams;
    for (const key of Object.keys(from) as (keyof DensityParams)[]) out[key] = lerp(from[key], to[key], blend);
    return out;
}

export function getDifficulty(input: DifficultyInput, cfg: DifficultyFile, mode: ModeConfig): DifficultyParams {
    const scale = input.grade === null
        ? freerideScale(input.distanceM, cfg)
        : cfg.gradeMultiplier[input.grade] * zoneRamp(input.zone, cfg);

    const base = blendedBase(input.zone, cfg);
    const out = {} as DifficultyParams;
    for (const key of Object.keys(base) as (keyof DensityParams)[]) {
        const modeScale = mode.difficultyScale[key] ?? 1;
        out[key] = Math.min(cfg.caps[key], base[key] * scale * modeScale);
    }
    out.bigfootAggression = input.bigfootAggression;
    out.scale = scale;
    return out;
}
